import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import apiService from '../../services/api';
import logger from '../../utils/logging/logger';
import { getCourseTimelineStatus } from '../courses/coursesHelpers';

/** Courses this instructor teaches (current by default, previous on request). */
export default function useInstructorDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [taughtCourses, setTaughtCourses] = useState([]);
  const [showInactiveCourses, setShowInactiveCourses] = useState(false);
  // Separate "not fetched yet" from "fetched and empty", so nobody sees
  // "No courses assigned yet." while the request is still in flight.
  const [loading, setLoading] = useState(true);

  const fetchInstructorData = useCallback(async () => {
    try {
      const response = await apiService.getCourses();
      if (response?.success && response.courses) {
        setTaughtCourses(response.courses.filter((course) => course.instructor_id === user.id));
      }
    } catch (error) {
      logger.error('Error fetching instructor courses', { error });
      setTaughtCourses([]);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (user) fetchInstructorData();
  }, [user, fetchInstructorData]);

  // Start at the top on phones.
  useEffect(() => {
    if (window.innerWidth <= 768) window.scrollTo(0, 0);
  }, []);

  // "Current" = switched on and not ended yet (an ended course can still have its switch on).
  const currentCourses = useMemo(() => taughtCourses.filter((course) => {
    const isActive = course.is_active === 1 || course.is_active === true;
    return isActive && !getCourseTimelineStatus(course).hasEnded;
  }), [taughtCourses]);

  const filteredCourses = showInactiveCourses ? taughtCourses : currentCourses;

  return {
    user,
    navigate,
    loading,
    taughtCourses,
    currentCourses,
    filteredCourses,
    showInactiveCourses,
    setShowInactiveCourses,
    refreshCourses: fetchInstructorData,
  };
}
